/**
 * LinkInstanceDialog - Modal para vincular instância a uma inbox.
 *
 * ARQUITETURA:
 * - Lista apenas inboxes compatíveis (mesmo provider) e sem instância vinculada.
 * - Permite selecionar uma inbox para vincular à instância.
 * - Usa RPC segura para vincular com validações de tenant.
 *
 * @version 2.1.0
 * RELEASE: 2026-08-16 v2.1.0 - CIRÚRGICO: substitui as consultas diretas às tabelas
 *                              `inboxes`/`provider_instances` (que quebravam com
 *                              22P02 porque a RPC de listagem não expõe mais o
 *                              provider_id) pela RPC segura
 *                              `get_eligible_inboxes_for_instance`.
 * RELEASE: 2026-06-29 v2.0.0 - Padronização visual premium replicando o
 *                              layout do AddInstanceDialog (header com
 *                              gradient + ícone badge, cards de inbox em
 *                              paleta sutil, footer `bg-muted/10`). Sem
 *                              alterações em lógica/queries/mutations.
 * RELEASE: 2026-03-05 v1.0.0 - Versão inicial.

 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Link2,
  AlertCircle,
  MessageCircle,
  CheckCircle2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useInstances, type Instance } from '@/hooks/useInstances';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

const logger = createLogger('LinkInstanceDialog');

/** Props do componente LinkInstanceDialog */
interface LinkInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: Instance | null;
  tenantId: string;
  onSuccess: () => void;
}

/** Interface para inbox elegível para vinculação */
interface EligibleInbox {
  id: string;
  name: string;
  identifier: string;
  status: string;
  channel_type: string;
}

export function LinkInstanceDialog({
  open,
  onOpenChange,
  instance,
  tenantId,
  onSuccess,
}: LinkInstanceDialogProps) {
  const [eligibleInboxes, setEligibleInboxes] = useState<EligibleInbox[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingInboxes, setLoadingInboxes] = useState(false);
  /** Mensagem de erro da busca (evita falha silenciosa no modal) */
  const [fetchError, setFetchError] = useState<string | null>(null);

  const { linkInstance, isLinking } = useInstances(tenantId);

  // Busca inboxes elegíveis ao abrir o dialog
  useEffect(() => {
    if (open && instance) {
      fetchEligibleInboxes();
    } else {
      setEligibleInboxes([]);
      setSelectedInboxId(null);
      setFetchError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instance]);

  /**
   * Busca inboxes elegíveis para receber a instância.
   * v2.1.0: toda a regra (mesmo provider, status compatível e sem vínculo)
   * roda no servidor via RPC segura — o frontend não conhece o provider_id.
   */
  const fetchEligibleInboxes = async () => {
    if (!instance || !tenantId) return;

    setLoadingInboxes(true);
    setFetchError(null);
    try {
      logger.debug('Buscando inboxes elegíveis via RPC', {
        instanceId: instance.id,
        channelType: instance.channel_type,
      });

      const { data, error } = await supabase.rpc(
        'get_eligible_inboxes_for_instance' as never,
        {
          p_instance_id: instance.id,
          p_tenant_hint: tenantId,
        } as never,
      );

      if (error) throw error;

      const eligible = (data as unknown as EligibleInbox[]) || [];
      logger.debug('Inboxes elegíveis encontradas', { count: eligible.length });
      setEligibleInboxes(eligible);
    } catch (error: any) {
      logger.error('Erro ao buscar inboxes elegíveis', { error });
      setFetchError(error?.message || 'Falha ao carregar inboxes elegíveis');
      setEligibleInboxes([]);
    } finally {
      setLoadingInboxes(false);
    }
  };


  /** Vincula a instância à inbox selecionada */
  const handleLink = async () => {
    if (!instance || !selectedInboxId) return;

    setLoading(true);
    try {
      await linkInstance({ instanceId: instance.id, inboxId: selectedInboxId });
      onSuccess();
    } catch {
      // Erro já tratado no hook
    } finally {
      setLoading(false);
    }
  };

  if (!instance) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl p-0 gap-0 overflow-hidden flex flex-col"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Link2 className="h-3.5 w-3.5 text-primary" />
              </div>
              Vincular instância a inbox
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Selecione uma inbox compatível para vincular à instância
              "{instance.instance_name}".
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO ===== */}
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Card de contexto da instância */}
          <div className="rounded-lg border p-3 flex items-center gap-3 bg-muted/20">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <MessageCircle className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">
                {instance.instance_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {instance.provider_display_name}
              </p>
            </div>
            <Badge
              variant="outline"
              className="text-[10px] font-normal text-muted-foreground border-border"
            >
              {instance.channel_type}
            </Badge>
          </div>

          {/* Loading */}
          {loadingInboxes && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Vazio / erro */}
          {!loadingInboxes && eligibleInboxes.length === 0 && (
            <div className="rounded-lg border border-dashed p-6 text-center space-y-2">
              <AlertCircle
                className={cn(
                  'h-8 w-8 mx-auto',
                  fetchError ? 'text-destructive' : 'text-muted-foreground',
                )}
              />
              <p className="text-sm font-medium text-foreground">
                {fetchError ? 'Não foi possível carregar as inboxes' : 'Nenhuma inbox disponível'}
              </p>
              <p className="text-xs text-muted-foreground">
                {fetchError ? (
                  fetchError
                ) : (
                  <>
                    Crie uma inbox do tipo <strong>{instance.channel_type}</strong> sem
                    instância vinculada para liberar a vinculação.
                  </>
                )}
              </p>
            </div>
          )}


          {/* Lista de inboxes elegíveis (mesmo padrão visual do AddInstanceDialog) */}
          {!loadingInboxes &&
            eligibleInboxes.map((inbox) => {
              const isSelected = selectedInboxId === inbox.id;
              return (
                <button
                  type="button"
                  key={inbox.id}
                  onClick={() => setSelectedInboxId(inbox.id)}
                  className={cn(
                    'w-full text-left rounded-lg border p-4 transition-all flex items-center gap-3',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border hover:bg-muted/30',
                  )}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    <MessageCircle className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {inbox.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {inbox.identifier}
                    </p>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  )}
                </button>
              );
            })}
        </div>

        {/* ===== FOOTER PREMIUM ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={loading || isLinking}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleLink}
            disabled={!selectedInboxId || loading || isLinking}
          >
            {loading || isLinking ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
            )}
            Vincular
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
